import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Search, SlidersHorizontal } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { FiligreeFrame } from "@/components/brand/FiligreeFrame";
import { ProductCard, type ProductCardData } from "@/components/store/ProductCard";
import { listProducts } from "@/lib/store.functions";
import { seo } from "@/lib/seo";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/store/")({
  head: () =>
    seo({
      title: "The Currency Thoughts Store",
      description:
        "Curated teachings, workbooks, and study guides for generational stewardship — household governance, planning, and inheritance.",
      canonical: "/store",
    }),
  component: StoreIndex,
});

const PRODUCT_TYPES = [
  { value: "teaching", label: "Teaching" },
  { value: "workbook", label: "Workbook" },
  { value: "course", label: "Course" },
  { value: "study-guide", label: "Study Guide" },
  { value: "audio", label: "Audio" },
  { value: "video", label: "Video" },
  { value: "bundle", label: "Bundle" },
];

const CATEGORIES = [
  { value: "stewardship", label: "Stewardship" },
  { value: "household", label: "Household" },
  { value: "estate", label: "Estate" },
  { value: "doctrine", label: "Doctrine" },
  { value: "family-ed", label: "Family Education" },
  { value: "practical", label: "Practical" },
];

function StoreIndex() {
  const fetchProducts = useServerFn(listProducts);
  const { data: products, isLoading } = useQuery({
    queryKey: ["store", "products"],
    queryFn: () => fetchProducts(),
  });

  const [search, setSearch] = useState("");
  const [types, setTypes] = useState<string[]>([]);
  const [cats, setCats] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 200]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const featured = useMemo(
    () =>
      ((products ?? []) as Array<ProductCardData & { featured?: boolean }>)
        .filter((p) => Boolean(p.featured))
        .slice(0, 3),
    [products],
  );

  const filtered = useMemo(() => {
    if (!products) return [];
    const q = search.trim().toLowerCase();
    return (products as ProductCardData[]).filter((p) => {
      if (q) {
        const hay = `${p.title} ${p.subtitle ?? ""} ${p.description ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (types.length && !types.includes(p.product_type)) return false;
      if (cats.length && (!p.category || !cats.includes(p.category))) return false;
      const priceUsd = p.price_cents / 100;
      if (priceUsd < priceRange[0] || priceUsd > priceRange[1]) return false;
      return true;
    });
  }, [products, search, types, cats, priceRange]);

  const toggle = (arr: string[], v: string) =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* HERO */}
      <FiligreeFrame className="bg-navy-900 py-24 text-cream-50">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Eyebrow color="cream" align="center">
            The Currency Thoughts Store
          </Eyebrow>
          <h1 className="mt-6 font-serif text-4xl font-bold leading-[1.1] text-cream-50 md:text-[56px]">
            Resources for{" "}
            <em className="font-serif italic text-bronze-400">
              generational stewardship.
            </em>
          </h1>
          <p className="mx-auto mt-6 max-w-[560px] font-sans text-[17px] leading-relaxed text-cream-50/85">
            Teaching, workbooks, and study guides for families building durable
            wealth — household governance, responsible stewardship of resources,
            and generational inheritance.
          </p>

          <div className="mx-auto mt-10 flex max-w-xl items-center gap-3 rounded-full bg-cream-50 px-5 py-3 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.35)]">
            <Search className="h-4 w-4 text-charcoal-500" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the library..."
              className="w-full bg-transparent font-sans text-sm text-charcoal-900 placeholder:text-charcoal-500 focus:outline-none"
              aria-label="Search the library"
            />
          </div>
        </div>
      </FiligreeFrame>

      {/* FEATURED */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <Eyebrow>Featured Resources</Eyebrow>
        {isLoading ? (
          <p className="mt-8 font-sans text-charcoal-500">Loading the library…</p>
        ) : featured.length === 0 ? (
          <p className="mt-6 font-sans text-sm italic text-charcoal-500">
            Featured resources are being curated.
          </p>
        ) : (
          <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* BROWSE */}
      <section className="mx-auto max-w-6xl px-6 pb-16 pt-4">
        <Eyebrow>Browse the Store</Eyebrow>

        <div className="mt-8 grid gap-10 lg:grid-cols-[240px_1fr]">
          {/* Mobile filters */}
          <Collapsible
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            className="lg:hidden"
          >
            <CollapsibleTrigger className="flex w-full items-center justify-between border border-bronze-600/30 bg-white px-4 py-3 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-navy-800">
              <span className="inline-flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4" /> Filters
              </span>
              <span className="text-bronze-600">{filtersOpen ? "—" : "+"}</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <FilterPanel
                types={types}
                setTypes={setTypes}
                cats={cats}
                setCats={setCats}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                toggle={toggle}
              />
            </CollapsibleContent>
          </Collapsible>

          {/* Desktop sidebar */}
          <aside className="hidden lg:block">
            <FilterPanel
              types={types}
              setTypes={setTypes}
              cats={cats}
              setCats={setCats}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              toggle={toggle}
            />
          </aside>

          <div>
            {isLoading ? (
              <p className="font-sans text-charcoal-500">Loading…</p>
            ) : filtered.length === 0 ? (
              <div className="border border-bronze-600/20 bg-white p-12 text-center">
                <h3 className="font-serif text-xl text-navy-800">
                  No resources match your selection.
                </h3>
                <p className="mt-2 font-sans text-sm text-charcoal-500">
                  Adjust the filters or clear your search.
                </p>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

interface FilterPanelProps {
  types: string[];
  setTypes: (v: string[]) => void;
  cats: string[];
  setCats: (v: string[]) => void;
  priceRange: [number, number];
  setPriceRange: (v: [number, number]) => void;
  toggle: (arr: string[], v: string) => string[];
}

function FilterPanel({
  types,
  setTypes,
  cats,
  setCats,
  priceRange,
  setPriceRange,
  toggle,
}: FilterPanelProps) {
  return (
    <div className="space-y-8 border border-bronze-600/20 bg-white p-5">
      <FilterGroup label="Product Type">
        <div className="space-y-2.5">
          {PRODUCT_TYPES.map((t) => (
            <FilterCheckbox
              key={t.value}
              id={`type-${t.value}`}
              label={t.label}
              checked={types.includes(t.value)}
              onCheckedChange={() => setTypes(toggle(types, t.value))}
            />
          ))}
        </div>
      </FilterGroup>

      <FilterGroup label="Category">
        <div className="space-y-2.5">
          {CATEGORIES.map((c) => (
            <FilterCheckbox
              key={c.value}
              id={`cat-${c.value}`}
              label={c.label}
              checked={cats.includes(c.value)}
              onCheckedChange={() => setCats(toggle(cats, c.value))}
            />
          ))}
        </div>
      </FilterGroup>

      <FilterGroup label="Price Range">
        <div className="px-1">
          <Slider
            value={priceRange}
            onValueChange={(v) => setPriceRange([v[0] ?? 0, v[1] ?? 200])}
            min={0}
            max={200}
            step={5}
            minStepsBetweenThumbs={1}
          />
          <div className="mt-3 flex justify-between font-sans text-[12px] text-charcoal-500">
            <span>${priceRange[0]}</span>
            <span>${priceRange[1]}{priceRange[1] === 200 ? "+" : ""}</span>
          </div>
        </div>
      </FilterGroup>
    </div>
  );
}

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
        {label}
      </p>
      <div className="mt-3 border-t border-bronze-600/20 pt-3">{children}</div>
    </div>
  );
}

function FilterCheckbox({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: () => void;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-center gap-2.5 font-sans text-[13px] text-charcoal-900",
        "transition hover:text-navy-800",
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
      {label}
    </label>
  );
}